import { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { 
  Users, 
  CreditCard, 
  TrendingUp, 
  DollarSign, 
  Calendar,
  Activity,
  Brain,
  UserPlus,
  Crown,
  Clock
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AdminDashboard = ({ token }) => {
  const [dashboardData, setDashboardData] = useState(null);
  const [usersList, setUsersList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const [dashboardRes, usersRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/admin/dashboard`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${BACKEND_URL}/api/admin/users`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      
      setDashboardData(dashboardRes.data);
      setUsersList(usersRes.data.users || []);
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error('Admin access required');
      } else {
        toast.error('Failed to load admin data');
      }
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-muted-foreground">Loading admin dashboard...</div>
      </div>
    );
  }

  if (!dashboardData) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-red-500">Access denied. Admin privileges required.</div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-dashboard">
      <div>
        <h1 className="text-4xl font-heading font-bold tracking-tight">Admin Dashboard</h1>
        <p className="text-muted-foreground font-body">Monitor users, subscriptions, and revenue</p>
      </div>

      {/* Stats Overview */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-gradient-to-br from-blue-500 to-blue-600 text-white rounded-lg p-6 shadow-lg">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-blue-100 text-sm font-medium">Total Users</p>
              <p className="text-3xl font-bold mt-1">{dashboardData.users.total}</p>
            </div>
            <Users className="h-10 w-10 text-blue-200" />
          </div>
          <div className="mt-3 text-sm text-blue-100">
            +{dashboardData.users.today} today, +{dashboardData.users.this_week} this week
          </div>
        </div>

        <div className="bg-gradient-to-br from-green-500 to-green-600 text-white rounded-lg p-6 shadow-lg">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-green-100 text-sm font-medium">Active Subscribers</p>
              <p className="text-3xl font-bold mt-1">{dashboardData.users.subscribers}</p>
            </div>
            <Crown className="h-10 w-10 text-green-200" />
          </div>
          <div className="mt-3 text-sm text-green-100">
            {dashboardData.subscriptions.monthly} monthly, {dashboardData.subscriptions.yearly} yearly
          </div>
        </div>

        <div className="bg-gradient-to-br from-purple-500 to-purple-600 text-white rounded-lg p-6 shadow-lg">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-purple-100 text-sm font-medium">Monthly Revenue</p>
              <p className="text-3xl font-bold mt-1">${dashboardData.subscriptions.total_revenue}</p>
            </div>
            <DollarSign className="h-10 w-10 text-purple-200" />
          </div>
          <div className="mt-3 text-sm text-purple-100">
            ${dashboardData.subscriptions.monthly_revenue}/mo + ${dashboardData.subscriptions.yearly_revenue}/yr
          </div>
        </div>

        <div className="bg-gradient-to-br from-orange-500 to-orange-600 text-white rounded-lg p-6 shadow-lg">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-orange-100 text-sm font-medium">AI Usage Cost</p>
              <p className="text-3xl font-bold mt-1">${dashboardData.activity.total_ai_cost}</p>
            </div>
            <Brain className="h-10 w-10 text-orange-200" />
          </div>
          <div className="mt-3 text-sm text-orange-100">
            {dashboardData.activity.total_ai_calls} AI calls made
          </div>
        </div>
      </div>

      {/* User Breakdown */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="bg-card border rounded-lg p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="h-10 w-10 rounded-full bg-green-100 flex items-center justify-center">
              <Crown className="h-5 w-5 text-green-600" />
            </div>
            <div>
              <p className="font-semibold">Subscribers</p>
              <p className="text-2xl font-bold text-green-600">{dashboardData.users.subscribers}</p>
            </div>
          </div>
          <div className="text-sm text-muted-foreground">Paying customers</div>
        </div>

        <div className="bg-card border rounded-lg p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center">
              <Clock className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <p className="font-semibold">Trial Users</p>
              <p className="text-2xl font-bold text-blue-600">{dashboardData.users.trial}</p>
            </div>
          </div>
          <div className="text-sm text-muted-foreground">In 14-day free trial</div>
        </div>

        <div className="bg-card border rounded-lg p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="h-10 w-10 rounded-full bg-gray-100 flex items-center justify-center">
              <Users className="h-5 w-5 text-gray-600" />
            </div>
            <div>
              <p className="font-semibold">Free Users</p>
              <p className="text-2xl font-bold text-gray-600">{dashboardData.users.free}</p>
            </div>
          </div>
          <div className="text-sm text-muted-foreground">Not subscribed</div>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b">
        <div className="flex gap-4">
          <button
            onClick={() => setActiveTab('overview')}
            className={`pb-3 px-1 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'overview' 
                ? 'border-primary text-primary' 
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            Overview
          </button>
          <button
            onClick={() => setActiveTab('users')}
            className={`pb-3 px-1 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'users' 
                ? 'border-primary text-primary' 
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            All Users ({usersList.length})
          </button>
        </div>
      </div>

      {/* Tab Content */}
      {activeTab === 'overview' && (
        <div className="bg-card border rounded-lg p-6">
          <h3 className="font-semibold text-lg mb-4">Platform Activity</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="text-center p-4 bg-muted/50 rounded-lg">
              <Activity className="h-6 w-6 mx-auto text-blue-500 mb-2" />
              <p className="text-2xl font-bold">{dashboardData.activity.total_transactions}</p>
              <p className="text-xs text-muted-foreground">Total Transactions</p>
            </div>
            <div className="text-center p-4 bg-muted/50 rounded-lg">
              <UserPlus className="h-6 w-6 mx-auto text-green-500 mb-2" />
              <p className="text-2xl font-bold">{dashboardData.users.today}</p>
              <p className="text-xs text-muted-foreground">Signups Today</p>
            </div>
            <div className="text-center p-4 bg-muted/50 rounded-lg">
              <Calendar className="h-6 w-6 mx-auto text-purple-500 mb-2" />
              <p className="text-2xl font-bold">{dashboardData.users.this_week}</p>
              <p className="text-xs text-muted-foreground">Signups This Week</p>
            </div>
            <div className="text-center p-4 bg-muted/50 rounded-lg">
              <Brain className="h-6 w-6 mx-auto text-orange-500 mb-2" />
              <p className="text-2xl font-bold">{dashboardData.activity.total_ai_calls}</p>
              <p className="text-xs text-muted-foreground">AI Categorizations</p>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'users' && (
        <div className="bg-card border rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-muted/50">
                <tr>
                  <th className="text-left p-4 font-medium text-sm">Name</th>
                  <th className="text-left p-4 font-medium text-sm">Email</th>
                  <th className="text-left p-4 font-medium text-sm">Phone</th>
                  <th className="text-left p-4 font-medium text-sm">Status</th>
                  <th className="text-left p-4 font-medium text-sm">Joined</th>
                </tr>
              </thead>
              <tbody>
                {usersList.map((user, index) => {
                  const sub = user.subscription || {};
                  const status = sub.status === 'active' ? 'Subscriber' : 
                                 sub.trial_ends_at ? 'Trial' : 'Free';
                  const statusColor = status === 'Subscriber' ? 'bg-green-100 text-green-700' :
                                      status === 'Trial' ? 'bg-blue-100 text-blue-700' :
                                      'bg-gray-100 text-gray-700';
                  
                  return (
                    <tr key={user.id || index} className="border-t hover:bg-muted/30">
                      <td className="p-4 font-medium">{user.name || '-'}</td>
                      <td className="p-4 text-muted-foreground">{user.email}</td>
                      <td className="p-4 text-muted-foreground">{user.phone || '-'}</td>
                      <td className="p-4">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${statusColor}`}>
                          {status}
                        </span>
                      </td>
                      <td className="p-4 text-muted-foreground text-sm">
                        {user.created_at ? new Date(user.created_at).toLocaleDateString() : '-'}
                      </td>
                    </tr>
                  );
                })}
                {usersList.length === 0 && (
                  <tr>
                    <td colSpan={5} className="p-8 text-center text-muted-foreground">
                      No users found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;
